import React, { Suspense, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  Collapse,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  DirectionsRun,
  RouteOutlined,
  People,
  Notifications,
  Map,
  GridOn,
  Flag,
  EmojiEvents,
  Business,
  AccountTree,
  Assessment,
  Logout,
  PlayCircle,
  Analytics,
  Insights,
  Link as LinkShortIcon,
  BuildCircle,
  WorkspacePremium,
  Public,
  Category,
  Language,
  ExpandMore,
  ChevronRight,
  SyncAlt,
} from '@mui/icons-material';
import { useAuthStore } from '@/store/authStore';
import UserGlobalSearch from '@/components/UserGlobalSearch';
import { labelForPath, useTrackNavigation } from '@/navigation/navHistory';
import { tokens } from '@/theme/theme';
import { getEnvironment } from '@/utils/environment';
import logoUrl from '@/assets/runnrz-logo-square.png';

const drawerWidth = 248;

interface MenuItemDef {
  text: string;
  icon: React.ReactNode;
  path: string;
}

interface MenuSection {
  title?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  items: MenuItemDef[];
}

const menuSections: MenuSection[] = [
  {
    items: [
      { text: 'Tableau de bord', icon: <Analytics />, path: '/dashboard' },
      { text: 'KPI mensuels', icon: <Insights />, path: '/kpi' },
      { text: 'Premium & hard users', icon: <WorkspacePremium />, path: '/kpi/segments' },
    ],
  },
  {
    title: 'Utilisateurs',
    collapsible: true,
    defaultOpen: true,
    items: [
      { text: 'Utilisateurs', icon: <People />, path: '/utilisateurs' },
      { text: 'Parcours', icon: <DirectionsRun />, path: '/parcours' },
      { text: 'Parcours V2', icon: <RouteOutlined />, path: '/parcours-v2' },
    ],
  },
  {
    title: 'Marketing',
    collapsible: true,
    items: [
      { text: 'Défis', icon: <EmojiEvents />, path: '/challenges' },
      { text: 'Sponsors', icon: <Business />, path: '/sponsors' },
      { text: 'Entités', icon: <AccountTree />, path: '/entities' },
      { text: 'Liens courts', icon: <LinkShortIcon />, path: '/short-links' },
    ],
  },
  {
    title: 'Suivi',
    collapsible: true,
    items: [
      { text: 'Carte des exclusions', icon: <Map />, path: '/map' },
      { text: 'Grille du réseau', icon: <GridOn />, path: '/map-cells' },
      { text: 'Signalements', icon: <Assessment />, path: '/reports' },
      { text: 'Alertes', icon: <Notifications />, path: '/signalements' },
    ],
  },
  {
    title: 'Points d’intérêt',
    collapsible: true,
    items: [
      { text: 'Zones', icon: <Public />, path: '/poi-zones' },
      { text: 'Catégories', icon: <Category />, path: '/poi-categories' },
      { text: 'Points', icon: <Flag />, path: '/pois' },
    ],
  },
  {
    title: 'Technique',
    collapsible: true,
    items: [
      { text: 'Langues', icon: <Language />, path: '/languages' },
      { text: 'Simulateur', icon: <PlayCircle />, path: '/simulator' },
      { text: 'Parcours V2 bloqués', icon: <BuildCircle />, path: '/parcours-v2/force-finish' },
      { text: 'Réconciliation RC', icon: <SyncAlt />, path: '/revenuecat' },
      { text: 'Tableau de bord V1', icon: <Dashboard />, path: '/dashboard-v1' },
    ],
  },
];

const allMenuItems = menuSections.flatMap((section) => section.items);
const allMenuPaths = allMenuItems.map((item) => item.path);

const isPathActive = (pathname: string, path: string) => {
  if (pathname === path) return true;
  if (!pathname.startsWith(`${path}/`)) return false;
  const moreSpecificPaths = allMenuPaths.filter((p) => p !== path && p.startsWith(`${path}/`));
  return !moreSpecificPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};

interface DefaultLayoutProps {
  children?: React.ReactNode;
}

const DefaultLayout: React.FC<DefaultLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const location = useLocation();
  useTrackNavigation();
  const { user, destroySession } = useAuthStore();
  const env = useMemo(getEnvironment, []);

  const handleLogout = async () => {
    await destroySession();
    navigate('/login');
  };

  const sectionOpen = (section: MenuSection) => {
    const hasActive = section.items.some((item) => isPathActive(location.pathname, item.path));
    if (section.title && expanded[section.title] !== undefined) return expanded[section.title];
    if (hasActive) return true;
    return section.defaultOpen ?? false;
  };

  const toggleSection = (section: MenuSection) => {
    if (!section.title) return;
    setExpanded((prev) => ({ ...prev, [section.title!]: !sectionOpen(section) }));
  };

  const activeSection = menuSections.find((section) =>
    section.items.some((item) => isPathActive(location.pathname, item.path))
  );
  const activeItem = allMenuItems.find((item) => isPathActive(location.pathname, item.path));

  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || 'Admin';
  const initials =
    `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() ||
    user.email?.[0]?.toUpperCase() ||
    'A';

  const renderMenuItem = (item: MenuItemDef) => {
    const selected = isPathActive(location.pathname, item.path);
    return (
      <ButtonBase
        key={item.path}
        component={RouterLink}
        to={item.path}
        onClick={() => setMobileOpen(false)}
        aria-current={selected ? 'page' : undefined}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          gap: 1.25,
          height: 34,
          px: 1.25,
          borderRadius: 2,
          fontSize: '0.84375rem',
          fontWeight: selected ? 500 : 400,
          color: selected ? tokens.accentText : '#3A3935',
          bgcolor: selected ? tokens.accentSoft : 'transparent',
          transition: 'background-color .12s',
          '&:hover': { bgcolor: selected ? tokens.accentSoft : tokens.bg },
          '& .MuiSvgIcon-root': { fontSize: 18, color: selected ? tokens.accentText : '#6B6A64' },
        }}
      >
        {item.icon}
        <Box component="span" sx={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.text}
        </Box>
      </ButtonBase>
    );
  };

  const drawer = (
    <Box
      component="nav"
      aria-label="Navigation principale"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 1.5, py: 2, gap: 2.25, bgcolor: tokens.surface }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 0.75 }}>
        <Box component="img" src={logoUrl} alt="" aria-hidden sx={{ width: 32, height: 32, borderRadius: '8px', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2, letterSpacing: '-0.01em' }}>RunnrZ</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.3 }}>Administration</Typography>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            fontSize: 11,
            fontWeight: 500,
            color: env.color,
            bgcolor: env.bg,
            px: 0.9,
            py: 0.3,
            borderRadius: 999,
          }}
        >
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: env.color }} />
          {env.label}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, flex: 1, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
        {menuSections.map((section, index) => {
          const open = section.collapsible && section.title ? sectionOpen(section) : true;
          return (
            <Box key={section.title || `section-${index}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {section.title && (
                <ButtonBase
                  onClick={() => toggleSection(section)}
                  aria-expanded={open}
                  sx={{
                    height: 28,
                    px: 1.25,
                    borderRadius: 1.5,
                    justifyContent: 'space-between',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {section.title}
                  <ExpandMore
                    sx={{ fontSize: 16, transition: 'transform .15s', transform: open ? 'none' : 'rotate(-90deg)' }}
                  />
                </ButtonBase>
              )}
              <Collapse in={open} timeout="auto">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>{section.items.map(renderMenuItem)}</Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ borderTop: `1px solid ${tokens.lineSoft}`, pt: 1.75, px: 0.75, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Avatar src={(user as { avatar_url?: string }).avatar_url} sx={{ width: 32, height: 32, bgcolor: tokens.blueSoft, color: tokens.blue }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }} title={user.email || undefined}>
            {displayName}
          </Typography>
          <Link
            component={RouterLink}
            to="/account/security"
            underline="hover"
            onClick={() => setMobileOpen(false)}
            sx={{ fontSize: 11.5, color: 'text.secondary' }}
          >
            Sécurité (2FA)
          </Link>
        </Box>
        <Tooltip title="Déconnexion">
          <IconButton
            onClick={handleLogout}
            aria-label="Déconnexion"
            size="small"
            sx={{ width: 32, height: 32, border: `1px solid ${tokens.line}` }}
          >
            <Logout sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="aside" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            minHeight: 60,
            px: { xs: 2, md: 4 },
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'rgba(255,255,255,.92)',
            backdropFilter: 'saturate(1.4) blur(8px)',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <IconButton
            aria-label="Ouvrir le menu"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="nav"
            aria-label="Fil d’Ariane"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: { xs: 1, sm: '1 1 200px' }, fontSize: 13 }}
          >
            {activeSection?.title && activeSection.title !== activeItem?.text && (
              <>
                <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline' } }}>
                  {activeSection.title}
                </Box>
                <ChevronRight sx={{ fontSize: 16, color: '#A8A6A0', display: { xs: 'none', sm: 'inline-block' } }} />
              </>
            )}
            <Box component="span" sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {activeItem?.text || labelForPath(location.pathname)}
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 360px', maxWidth: 460, display: { xs: 'none', sm: 'flex' } }}>
            <UserGlobalSearch />
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            maxWidth: 1440,
            boxSizing: 'border-box',
            px: { xs: 2, md: 4 },
            pt: { xs: 2.5, md: 3.5 },
            pb: 7,
          }}
        >
          <Suspense fallback={<LinearProgress sx={{ mt: 1 }} />}>{children || <Outlet />}</Suspense>
        </Box>
      </Box>
    </Box>
  );
};

export default DefaultLayout;
